import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import {
  HistoryEduOutlinedIcon,
  MoreVertIcon,
  NoteAddOutlinedIcon,
  RestartAltIcon,
} from "icons";
import theme from "theme";
import { addQueryParams, reverse } from "utils/urls";

import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import Link from "components/Link";
import { MenuItem } from "components/Menu";
import NoPrint from "components/NoPrint";
import PageHeader from "components/PageHeader";
import Stack from "components/Stack";
import Text from "components/Text";

import { useSignedUser } from "entities/Auth/sdk";
import ESignatureTag from "entities/Memo/components/ESignatureTag";
import { MemoSignatureTagChoices } from "entities/Memo/components/ESignatureTag/constants";
import ShareButton from "entities/Memo/components/ShareButton";
import { MemoSnapshotCreationMethod } from "entities/Memo/components/Tabs/constants";

interface IMemoPageHeader {
  dealId: number;
  memoId: number;
  memoName: string;
  dealName: string;
  isShareableLink?: boolean;
  token?: string;
  isSignatureSectionPresentInMemo?: boolean;
  signatureStatus?: MemoSignatureTagChoices;
  handleESignatureDrawerOpen?: () => void;
  onRestart?: () => void;
}

const MemoPageHeader = ({
  dealId,
  memoId,
  memoName,
  dealName,
  onRestart,
  isShareableLink,
  token,
  isSignatureSectionPresentInMemo = false,
  signatureStatus,
  handleESignatureDrawerOpen,
}: IMemoPageHeader) => {
  const { data: user } = useSignedUser();

  const navigate = useNavigate();

  const setPrintableTitle = useCallback(
    () =>
      (window.document.title = `Builders Patch - ${dealName} - ${memoName} - Memo`),
    [dealName, memoName]
  );

  const setDefaultTitle = useCallback(
    () =>
      (window.document.title = `Builders Patch: ${dealName} - ${memoName} - Memo`),
    [dealName, memoName]
  );

  useEffect(() => {
    window.addEventListener("beforeprint", setPrintableTitle);
    window.addEventListener("afterprint", setDefaultTitle);

    return () => {
      window.removeEventListener("beforeprint", setPrintableTitle);
      window.removeEventListener("afterprint", setDefaultTitle);
    };
  }, [setPrintableTitle, setDefaultTitle]);

  const navigateToPrintableMemoPage = useCallback(
    () =>
      !_.isNil(token) && isShareableLink
        ? navigate(
            reverse(URLS.PRINTABLE_SHAREABLE_MEMO, {
              token,
            })
          )
        : navigate(
            addQueryParams(
              reverse(URLS.PRINTABLE_DEAL_MEMO, {
                dealId,
                memoId,
              }),
              { creationMethod: MemoSnapshotCreationMethod.DEFAULT }
            )
          ),
    [dealId, memoId, navigate, token, isShareableLink]
  );

  const memoSignatureProcessButtonText = useMemo(() => {
    if (signatureStatus === MemoSignatureTagChoices.E_SIGN_ENABLED) {
      return "Start e-sign process";
    } else if (signatureStatus === MemoSignatureTagChoices.SIGNED) {
      return "View signed document";
    }
    return "Track e-sign process";
  }, [signatureStatus]);

  return (
    <NoPrint>
      <PageHeader
        title={
          <Stack gap={1}>
            <Text variant="h2">{memoName}</Text>
            {isSignatureSectionPresentInMemo && !_.isNil(signatureStatus) && (
              <Stack direction="row" gap={1.5}>
                <ESignatureTag signatureStatus={signatureStatus} />
                <Button
                  color="secondary"
                  variant="outlined"
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    width: "fit-content",
                    padding: theme.spacing(0.5, 1),
                  }}
                  onClick={handleESignatureDrawerOpen}
                >
                  <Text variant="text3" fontWeight={500}>
                    {memoSignatureProcessButtonText}
                  </Text>
                  <HistoryEduOutlinedIcon width="16px" height="16px" />
                </Button>
                {signatureStatus === MemoSignatureTagChoices.SIGNED && (
                  <Button
                    color="secondary"
                    variant="outlined"
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      width: "fit-content",
                      padding: theme.spacing(0.5, 1),
                    }}
                    onClick={onRestart}
                  >
                    <Text variant="text3" fontWeight={500}>
                      Restart e-sign process
                    </Text>
                    <RestartAltIcon width="16px" height="16px" />
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1}>
            {!isShareableLink && user?.is_superuser && (
              <ButtonDropdown
                icon={<MoreVertIcon />}
                label=""
                variant="outlined"
                color="secondary"
                width="auto"
                sx={{
                  padding: theme.spacing(0.5, 0.75),
                  "& .MuiButton-endIcon": {
                    margin: 0,
                  },
                }}
              >
                <Link
                  to={reverse(URLS.MEMO_TEMPLATE_EDITOR, {
                    memoId: memoId,
                  })}
                >
                  <MenuItem>
                    <Text variant="text2">Edit template</Text>
                  </MenuItem>
                </Link>
              </ButtonDropdown>
            )}
            {!isShareableLink && (
              <ShareButton dealId={dealId} memoId={memoId} />
            )}
            <Button
              onClick={navigateToPrintableMemoPage}
              variant="outlined"
              color="secondary"
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 1,
                alignItems: "center",
              }}
            >
              <Text variant="text2" fontWeight={500}>
                Create a saved PDF
              </Text>
              <NoteAddOutlinedIcon sx={{ width: 22, height: 22 }} />
            </Button>
          </Stack>
        }
      />
    </NoPrint>
  );
};

export default MemoPageHeader;
